import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import {
  SYMPTOM_STATES,
  parseSymptomsString,
  serializeSymptoms,
  cycleSymptomState,
} from "../../../utils/symptomTriState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../../ui/dialog";
import Button from "../../button-v1";
import Tooltip from "../../shared-modules/Tooltip";
import { parseSegmentsWithDateTags } from "../../../utils/parseBoldMarkers";
import {
  contentEditableToMarkerString,
  renderMarkerStringIntoContentEditable,
  setCaretAtDisplayOffset,
  markerStringDisplayLength,
  insertTypedTextAsPlain,
  wouldEmptyRow,
  caretDisplayOffset,
  isCaretOnEdgeLine,
} from "../../../utils/contentEditableMarkers";
import styles from "./symptomTriStateList.module.css";
import DeltaDateTag from "./DeltaDateTag";

const LINE_HEIGHT = 19;

// Symptom names use the same contentEditable-based editor as
// FreeTextSection - real bold, correct native cursor tracking - see
// utils/contentEditableMarkers.js for the full rationale/history. Unlike
// FreeTextSection, Enter here means "split into a new, separately-
// toggleable row" rather than "insert a line break within this field", so
// it's handled locally below instead of via that file's insertBreakAtCaret.
const SymptomNameEditor = ({
  value,
  placeholder,
  showPlaceholder,
  onChange,
  onSplit,
  onMergeUp,
  onRemove,
  onMove,
  onRef,
}) => {
  const ref = useRef(null);

  // Bug fix: compares directly against the DOM's own actual current
  // content instead of a separate "last value we set" ref - self-healing
  // regardless of how a mismatch happened, rather than only as correct as
  // every code path that's supposed to keep a cache ref updated. See
  // FreeTextSection's identical fix for the full history/symptom this
  // resolved (fields going visibly blank while their own character counter
  // still showed the real length, right after "Edit Careplan").
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (contentEditableToMarkerString(el) === (value || "")) return;
    renderMarkerStringIntoContentEditable(el, value);
  }, [value]);

  const handleInput = () => {
    const el = ref.current;
    if (!el) return;
    onChange(contentEditableToMarkerString(el));
  };

  // Typing right after (or inside) an AI-bold symptom name stays plain -
  // see insertTypedTextAsPlain. Native listener for the same reason as
  // FreeTextSection's (React 17's onBeforeInput carries no inputType).
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onNativeBeforeInput = (e) => {
      if (insertTypedTextAsPlain(e, el)) handleInput();
    };
    el.addEventListener("beforeinput", onNativeBeforeInput);
    return () => el.removeEventListener("beforeinput", onNativeBeforeInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange]);

  const handleKeyDown = (e) => {
    if (e.nativeEvent?.isComposing) return;
    const el = ref.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    // Up/Down from the row's first/last line moves to the row above/below,
    // keeping roughly the same position along the line.
    if ((e.key === "ArrowUp" || e.key === "ArrowDown") && !e.shiftKey) {
      const direction = e.key === "ArrowUp" ? -1 : 1;
      if (isCaretOnEdgeLine(el, direction) && onMove(direction, caretDisplayOffset(el))) {
        e.preventDefault();
      }
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const beforeRange = document.createRange();
      beforeRange.selectNodeContents(el);
      beforeRange.setEnd(range.endContainer, range.endOffset);
      const afterRange = document.createRange();
      afterRange.selectNodeContents(el);
      afterRange.setStart(range.endContainer, range.endOffset);
      const before = contentEditableToMarkerString(beforeRange.cloneContents());
      const after = contentEditableToMarkerString(afterRange.cloneContents());
      onSplit(before, after);
      return;
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      // Deleting a row's last character(s) removes the whole row - its
      // checkbox too - instead of leaving an empty one behind.
      const beforeRange = document.createRange();
      beforeRange.selectNodeContents(el);
      beforeRange.setEnd(range.startContainer, range.startOffset);
      const afterRange = document.createRange();
      afterRange.selectNodeContents(el);
      afterRange.setStart(range.endContainer, range.endOffset);
      const parts = {
        before: contentEditableToMarkerString(beforeRange.cloneContents()),
        after: contentEditableToMarkerString(afterRange.cloneContents()),
        collapsed: sel.isCollapsed,
      };
      if (wouldEmptyRow(e.key, parts)) {
        e.preventDefault();
        onRemove();
        return;
      }
    }

    if (e.key === "Backspace" && sel.isCollapsed) {
      const beforeRange = document.createRange();
      beforeRange.selectNodeContents(el);
      beforeRange.setEnd(range.endContainer, range.endOffset);
      if (beforeRange.cloneContents().textContent === "") {
        e.preventDefault();
        onMergeUp(contentEditableToMarkerString(el));
      }
      // Otherwise let the browser's native Backspace proceed - deleting a
      // real character from a real text/<strong> node needs no special
      // handling at all, unlike the old raw-asterisk textarea approach.
    }
  };

  return (
    <div style={{ position: "relative", flex: 1 }}>
      {showPlaceholder && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            padding: "2px 0",
            fontSize: "14px",
            lineHeight: `${LINE_HEIGHT}px`,
            color: "#94A3B8",
            pointerEvents: "none",
          }}
        >
          {placeholder}
        </span>
      )}
      <div
        ref={(el) => {
          ref.current = el;
          onRef && onRef(el);
        }}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        style={{
          width: "100%",
          outline: "none",
          color: "#565975",
          fontSize: "14px",
          lineHeight: `${LINE_HEIGHT}px`,
          fontFamily: "inherit",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          padding: "2px 0",
          minHeight: "20px",
        }}
      />
    </div>
  );
};

// SCRUM-788: renders a symptom's name (plus " — qualifier" when the backend
// supplied one) with the same bold/date-tag treatment as every other
// AI-populated field (see FreeTextSection) - a qualified symptom can itself
// carry "**...**(added MM/DD/YYYY)" markers, same as any other AI text.
const SymptomText = ({ symptom }) => {
  const display = symptom.qualifier ? `${symptom.name} — ${symptom.qualifier}` : symptom.name;
  return parseSegmentsWithDateTags(display).map((segment, i) =>
    segment.bold ? (
      <strong key={i} style={{ fontWeight: 700, color: "#6b7280" }}>
        {segment.text}
        <DeltaDateTag dateTag={segment.dateTag} />
      </strong>
    ) : (
      <React.Fragment key={i}>{segment.text}</React.Fragment>
    )
  );
};

// Elegant square toggle per SCRUM-755: a filled green check for a
// confirmed/positive symptom, a light red outline X for the default
// negative/not-reported state - clicking flips between exactly these two.
const StateGlyph = ({ state }) => {
  const isYes = state === SYMPTOM_STATES.YES;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "16px",
        height: "16px",
        borderRadius: "3px",
        backgroundColor: isYes ? "#10b981" : "#FEE2E2",
        color: isYes ? "#ffffff" : "#ef4444",
        border: isYes ? "none" : "1.5px solid #FCA5A5",
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: 1,
        flexShrink: 0,
        transition: "background-color 0.15s ease, color 0.15s ease",
      }}
    >
      {isYes ? "✓" : "X"}
    </span>
  );
};

const SymptomLegendInfo = () => (
  <Tooltip
    position="top"
    content={
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px" }}>
        <span>Click the checkbox once to mark <strong style={{ color: "#10b981" }}>Positive</strong> (✓), click again to mark it <strong style={{ color: "#ef4444" }}>Negative</strong> (X).</span>
        {/* <span><strong style={{ color: "#10b981" }}>✓</strong> Patient confirmed/reported symptom</span>
        <span><strong style={{ color: "#ef4444" }}>X</strong> Negative / no reported symptom</span> */}
      </div>
    }
  >
    <span style={{ display: "inline-flex", alignItems: "center", color: "#94A3B8", cursor: "pointer", paddingTop: "5px" }}>
      <Info size={14} />
    </span>
  </Tooltip>
);

// Two-state per-symptom control (SCRUM-755, simplified from SCRUM-738's
// original 3-state yes/no/unset version): every symptom defaults to
// negative (X); one click confirms it positive (check), a second click
// reverts it - while the symptom's own name stays freely editable text,
// same row/Enter/Backspace semantics as NumberedTextArea so adding/
// splitting/merging symptom names still works the same way (pressing Enter
// mid-symptom still splits it into a new, separately-toggleable row).
const SymptomTriStateList = ({
  label,
  placeholder = "Enter a symptom...",
  value = "",
  onChange,
  readOnly = false,
  minHeight = 60,
  showClear = true,
}) => {
  const symptoms = parseSymptomsString(value);
  const lineRefs = useRef([]);
  const pendingFocusRef = useRef(null);
  const containerRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Symptom rows are contentEditable divs now (see SymptomNameEditor above),
  // which size to their content natively - this only still matters for any
  // stray <textarea> elsewhere in this component (none currently), guarded
  // here rather than removed outright since it's a harmless no-op either way.
  const resizeLine = (el) => {
    if (!el || el.tagName !== "TEXTAREA") return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    if (readOnly) return;
    lineRefs.current.forEach(resizeLine);
    if (pendingFocusRef.current) {
      const { index, pos } = pendingFocusRef.current;
      pendingFocusRef.current = null;
      const el = lineRefs.current[index];
      if (el) {
        el.focus();
        // pos is a DISPLAY-text offset (see markerStringDisplayLength/setCaretAtDisplayOffset
        // above) - contentEditable has no setSelectionRange equivalent.
        setCaretAtDisplayOffset(el, pos);
      }
    }
  }, [value, readOnly]);

  // Same reasoning as FreeTextSection's own ResizeObserver: a plain
  // value-change effect measures against whatever width exists at that
  // instant, which can be wrong while the surrounding layout is still
  // settling (e.g. a modal still animating open) - this catches that
  // settling directly instead of waiting for an interaction to fix it.
  // Guarded to only react to actual width changes, since this container's
  // own height changes as a side effect of resizeLine() itself.
  const lastWidthRef = useRef(null);
  useLayoutEffect(() => {
    if (readOnly) return;
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentBoxSize?.[0]?.inlineSize ?? entries[0]?.contentRect?.width;
      if (width !== undefined && width === lastWidthRef.current) return;
      lastWidthRef.current = width;
      lineRefs.current.forEach(resizeLine);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [readOnly]);

  const emit = (newSymptoms) => onChange && onChange(serializeSymptoms(newSymptoms));

  const updateName = (index, name) => {
    const next = [...symptoms];
    next[index] = { ...next[index], name };
    emit(next);
  };

  const toggleState = (index) => {
    const next = [...symptoms];
    next[index] = { ...next[index], state: cycleSymptomState(next[index].state) };
    emit(next);
  };

  // SCRUM-788: a qualified (AI-provided qualifier) symptom renders as a
  // read-only pill instead of an editable name row (see the render below) -
  // its own delete control removes the whole row outright, same intent as
  // the "x" on any other removable tag/chip. Always leaves at least one
  // (possibly blank) row behind, same as the rest of this component.
  const removeSymptom = (index) => {
    const next = symptoms.filter((_, i) => i !== index);
    emit(next.length ? next : [{ name: "", state: SYMPTOM_STATES.NO, qualifier: null }]);
  };

  // Enter (split this row into two) and Backspace-at-start (merge into the
  // previous row) - same row/Enter/Backspace semantics as before, now
  // triggered by SymptomNameEditor's own DOM/Selection-based detection
  // (see its onSplit/onMergeUp) instead of a shared handler reading a
  // <textarea>'s selectionStart/selectionEnd.
  const handleSplit = (index, before, after) => {
    const next = [...symptoms];
    next[index] = { ...next[index], name: before };
    next.splice(index + 1, 0, { name: after, state: SYMPTOM_STATES.NO });
    pendingFocusRef.current = { index: index + 1, pos: 0 };
    emit(next);
  };

  // Up/Down: focus the nearest editable row in that direction (AI qualifier
  // pills have no editor, so they're skipped), at the same offset along the
  // line or its end if shorter. Returns false when there's no such row, so
  // the key keeps its native behavior.
  const handleMove = (index, direction, offset) => {
    for (let i = index + direction; i >= 0 && i < symptoms.length; i += direction) {
      const el = !symptoms[i].qualifier && lineRefs.current[i];
      if (el) {
        el.focus();
        setCaretAtDisplayOffset(el, Math.min(offset, markerStringDisplayLength(symptoms[i].name)));
        return true;
      }
    }
    return false;
  };

  // A row emptied by Backspace/Delete is removed outright; the caret moves to
  // the end of the row above (or the start of the new first row).
  const handleRemoveRow = (index) => {
    const prev = index > 0 ? symptoms[index - 1] : null;
    pendingFocusRef.current = prev
      ? { index: index - 1, pos: markerStringDisplayLength(prev.name) }
      : { index: 0, pos: 0 };
    removeSymptom(index);
  };

  const handleMergeUp = (index, currentContent) => {
    if (index === 0) return; // nothing above the first row to merge into
    const mergePos = markerStringDisplayLength(symptoms[index - 1].name);
    const next = [...symptoms];
    next[index - 1] = { ...next[index - 1], name: next[index - 1].name + currentContent };
    next.splice(index, 1);
    pendingFocusRef.current = { index: index - 1, pos: mergePos };
    emit(next);
  };

  if (readOnly) {
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "2px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {label && <label style={{ color: "#565975", fontSize: "14px" }}>{label}</label>}
          <SymptomLegendInfo />
        </div>
        <div
          style={{
            width: "100%",
            minHeight: `${minHeight}px`,
            boxSizing: "border-box",
            borderBottom: "1px solid #C9CBD5",
            padding: "9px 12px 9px 0",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          {symptoms.filter((symptom) => symptom.name.trim()).map((symptom, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {symptom.qualifier ? null : <StateGlyph state={symptom.state} />}
              <span
                style={{
                  color: "#0D1139",
                  fontSize: "14px",
                  lineHeight: `${LINE_HEIGHT}px`,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <SymptomText symptom={symptom} />
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasContent = symptoms.some((s) => s.name.trim());
  const isEmpty = symptoms.length === 1 && !symptoms[0].name.trim() && !symptoms[0].qualifier;

  const handleClear = () => {
    onChange && onChange("");
    setConfirmOpen(false);
  };

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "2px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {label && <label style={{ color: "#0D1139", fontSize: "14px" }}>{label}</label>}
          <SymptomLegendInfo />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "auto" }}>
          {showClear && hasContent ? (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              title={`Clear ${label || "section"}`}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#94A3B8",
                fontSize: "11px",
                lineHeight: 1,
                padding: "2px 4px",
              }}
            >
              ✕
            </button>
          ) : null}
        </div>
      </div>

      <div
        ref={containerRef}
        style={{
          width: "100%",
          minHeight: `${minHeight}px`,
          border: "1px solid #C9CBD5",
          borderRadius: "6px",
          background: "#F8FAFD",
          // 7px + each row editor's own 2px = text 9px from the top, same as
          // FreeTextSection, so the placeholder lines up with Prognosis.
          padding: "7px 12px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        {symptoms.map((symptom, i) =>
          // SCRUM-788: a symptom carrying an AI-supplied qualifier (e.g.
          // "headache — not reported") renders as a read-only pill with its
          // own delete control, instead of the editable toggle+textarea row
          // below - the qualifier is backend-sourced descriptive text, not
          // something meant to be retyped inline. A plain manually-added
          // symptom (no qualifier) keeps the existing row exactly as before.
          symptom.qualifier ? (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 8px",
                borderRadius: "12px",
                background: "#F1F5F9",
                border: "1px solid #C9CBD5",
                width: "fit-content",
                maxWidth: "100%",
              }}
            >
              <span
                style={{
                  color: "#0D1139",
                  fontSize: "14px",
                  lineHeight: `${LINE_HEIGHT}px`,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <SymptomText symptom={symptom} />
              </span>
              <button
                type="button"
                onClick={() => removeSymptom(i)}
                title={`Remove ${symptom.name}`}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#94A3B8",
                  fontSize: "11px",
                  lineHeight: 1,
                  padding: "2px",
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
            </div>
          ) : (
            <div key={i} className={styles.row}>
              {/* An empty field shows just the placeholder, lined up like
                  every other care plan field; the ✓/✕ toggle appears with
                  the first letter. The `null` slot keeps the editor at the
                  same child position, so it isn't remounted (and keeps
                  focus) when it does. In a non-empty list, a blank row keeps
                  the toggle's width so its text lines up. */}
              {isEmpty ? null : !symptom.name.trim() ? (
                <span style={{ width: "16px", flexShrink: 0 }} />
              ) : (
              <button
                type="button"
                onClick={() => toggleState(i)}
                // SCRUM-831: the icon's own current state, not generic toggle
                // instructions - ✗ describes the negative state it's showing,
                // ✓ describes the confirmed state it's showing.
                title={
                  symptom.state === SYMPTOM_STATES.YES
                    ? "patient-confirmed/reported symptom becomes visible."
                    : "negative/no reported symptom becomes visible."
                }
                style={{
                  background: "none",
                  border: "none",
                  padding: "2px 0 0",
                  marginTop: "3px",
                  cursor: "pointer",
                  lineHeight: 0,
                }}
              >
                <StateGlyph state={symptom.state} />
              </button>
              )}
              <SymptomNameEditor
                value={symptom.name}
                placeholder={placeholder}
                showPlaceholder={isEmpty}
                onChange={(name) => updateName(i, name)}
                onSplit={(before, after) => handleSplit(i, before, after)}
                onMergeUp={(content) => handleMergeUp(i, content)}
                onRemove={() => handleRemoveRow(i)}
                onMove={(direction, offset) => handleMove(i, direction, offset)}
                onRef={(el) => (lineRefs.current[i] = el)}
              />
              {/* Hover ✕ removes just this row (same as the list fields). */}
              {symptoms.length > 1 && (
                <button
                  type="button"
                  className={styles.removeRow}
                  onClick={() => removeSymptom(i)}
                  title="Remove symptom"
                  aria-label={`Remove symptom ${i + 1}`}
                >
                  ✕
                </button>
              )}
            </div>
          )
        )}
      </div>

      <Dialog open={confirmOpen} setOpen={setConfirmOpen}>
        <DialogContent style={{ maxWidth: "420px" }}>
          <DialogHeader>
            <DialogTitle>Clear {label || "Section"}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p style={{ color: "#565975", fontSize: "14px", lineHeight: 1.5, margin: 0 }}>
              {label || "This section"} will be cleared — the rest of the care
              plan is not affected. Are you sure you want to proceed?
            </p>
          </DialogBody>
          <DialogFooter style={{ justifyContent: "space-between", gap: "12px" }}>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              style={{ flex: 1, border: "1.5px solid #10164d", color: "#10164d", backgroundColor: "#ffffff" }}
            >
              No
            </Button>
            <Button variant="primary" onClick={handleClear} style={{ flex: 1 }}>
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SymptomTriStateList;
